import { zodResolver } from '@hookform/resolvers/zod'
import {
  Autocomplete,
  Button,
  Container,
  Group,
  Image,
  Loader,
  MultiSelect,
  NumberInput,
  Paper,
  PasswordInput,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { IconCircleCheck, IconCircleCheckFilled } from '@tabler/icons-react'
import { createFileRoute } from '@tanstack/react-router'
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { useRegisterProviderMutation } from '../api/auth/api'
import { useListMetroAreasQuery, useListVehicleClassesQuery, usePlacesAutocompleteQuery } from '../api/branches/api'
import { RouterButton } from '../components/RouterButton'

const businessInfoSchema = z.object({
  entityName: z.string().min(1, 'Legal entity name is required'),
  branchName: z.string().min(1, 'Branch name is required'),
  branchEmail: z.string().email('Invalid email').min(1, 'Branch email is required'),
  branchAddress: z.string().min(1, 'Branch address is required'),
  branchPhone: z
    .string()
    .min(1, 'Branch phone is required')
    .refine(
      (phone) => {
        const digitsOnly = phone.replace(/\D/g, '')
        if (digitsOnly.length !== 10) {
          return false
        }
        const expectedFormat = `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`
        return phone === expectedFormat
      },
      {
        message: 'Phone number must be in the format (xxx) xxx-xxxx',
      },
    ),
  branchMetro: z.string().min(1, 'Metro area is required'),
  branchEngineTypes: z.array(z.string()).min(1, 'Select at least one engine type'),
  branchVehicleClasses: z.array(z.string()).min(1, 'Select at least one vehicle class'),
  branchLaborRate: z
    .number({ invalid_type_error: 'Labor rate is required' })
    .min(1, 'Labor rate must be greater than 0'),
})

type BusinessInfoFormData = z.infer<typeof businessInfoSchema>

type RegisterFormData = {
  businessInfo: BusinessInfoFormData
  personalInfo?: {
    email: string
    phone: string
    password: string
    confirmPassword: string
  }
}

const personalInfoSchema = z
  .object({
    email: z.string().email('Invalid email'),
    phone: z
      .string()
      .min(1, 'Phone number is required')
      .refine(
        (phone) => {
          const digitsOnly = phone.replace(/\D/g, '')
          if (digitsOnly.length !== 10) {
            return false
          }
          const expectedFormat = `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`
          return phone === expectedFormat
        },
        {
          message: 'Phone number must be in the format (xxx) xxx-xxxx',
        },
      ),
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .max(32, 'Password must be at most 32 characters')
      .regex(/[A-Z]/, 'Must contain 1 uppercase letter')
      .regex(/[a-z]/, 'Must contain 1 lowercase letter')
      .regex(/[!@#$%^&*(),.?":{}|<>]/, 'Must contain 1 special character (!, @, #, $, %, etc.)'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

export const Route = createFileRoute('/register')({
  component: RegisterPage,
})

function RegisterPage() {
  const [activeStep, setActiveStep] = useState(0)

  // We'll eventually combine all steps' data here
  const [formData, setFormData] = useState<Partial<RegisterFormData>>({})
  const [stripeLink, setStripeLink] = useState<string | null>(null)

  // For branch address autocomplete
  const [branchAddressInput, setBranchAddressInput] = useState('')
  const {
    data: placesData,
    isLoading: isPlacesLoading,
    isError: isPlacesError,
  } = usePlacesAutocompleteQuery(branchAddressInput)

  // Step 1: Business Info form
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<BusinessInfoFormData>({
    resolver: zodResolver(businessInfoSchema),
    mode: 'onChange',
    defaultValues: formData.businessInfo || {
      entityName: '',
      branchName: '',
      branchEmail: '',
      branchAddress: '',
      branchPhone: '',
      branchMetro: '',
      branchEngineTypes: [],
      branchVehicleClasses: [],
      branchLaborRate: 0,
    },
  })

  // When going back to this step, restore form values from formData
  React.useEffect(() => {
    if (activeStep === 0 && formData.businessInfo) {
      reset(formData.businessInfo)
    }
  }, [activeStep, formData.businessInfo, reset])

  const onSubmit = handleSubmit((data) => {
    setFormData((prev) => ({ ...prev, businessInfo: data }))
    setActiveStep(1) // Move to next step (not yet implemented)
  })

  const laborRateValueRaw = watch('branchLaborRate')
  const laborRateValue =
    typeof laborRateValueRaw === 'number' && !isNaN(laborRateValueRaw) ? laborRateValueRaw : undefined

  // Fetch metro areas
  const { data: metroAreas, isError: isMetroError } = useListMetroAreasQuery()
  // Fetch vehicle classes
  const {
    data: vehicleClasses,
    isError: isVehicleClassesError,
    error: vehicleClassesError,
  } = useListVehicleClassesQuery()
  if (isVehicleClassesError) {
    console.error('Vehicle classes fetch error:', vehicleClassesError)
  }

  // Step 2: Personal Info form
  const {
    handleSubmit: handleSubmitPersonal,
    formState: { errors: errorsPersonal },
    setValue: setValuePersonal,
    watch: watchPersonal,
  } = useForm({
    resolver: zodResolver(personalInfoSchema),
    defaultValues: formData.personalInfo || {
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
    },
    mode: 'onChange',
  })

  const { mutate: registerProvider, isPending: isRegistering, isError: isRegisterError } = useRegisterProviderMutation()

  const onSubmitPersonal = handleSubmitPersonal((data) => {
    setFormData((prev) => ({ ...prev, personalInfo: data }))
    // Map formData to API payload
    const business = formData.businessInfo
    const personal = data
    if (!business) return
    const payload = {
      legal_entity_name: business.entityName,
      branch_name: business.branchName,
      branch_email: business.branchEmail,
      branch_address: business.branchAddress,
      branch_phone_Number: business.branchPhone.replace(/\D/g, ''),
      branch_metro_area_id: business.branchMetro,
      serviceable_engine_types: business.branchEngineTypes,
      serviceable_vehicle_class_ids: business.branchVehicleClasses,
      labor_rate: business.branchLaborRate,
      user_email: personal.email,
      user_phone_number: personal.phone.replace(/\D/g, ''),
      user_password: personal.password,
    }
    registerProvider(payload, {
      onSuccess: (data) => {
        setStripeLink(data.stripe_link || null)
        setActiveStep(2)
      },
    })
  })

  // When going back to this step, restore form values from formData
  React.useEffect(() => {
    if (activeStep === 1 && formData.personalInfo) {
      // Use setValue instead of reset to ensure controlled inputs are updated
      setValuePersonal('email', formData.personalInfo.email || '')
      setValuePersonal('phone', formData.personalInfo.phone || '')
      setValuePersonal('password', formData.personalInfo.password || '')
      setValuePersonal('confirmPassword', formData.personalInfo.confirmPassword || '')
    }
  }, [activeStep, formData.personalInfo, setValuePersonal])

  // Password requirements checklist
  const passwordValue = watchPersonal('password') || ''
  const passwordChecks = [
    {
      label: 'Be at least 8 characters long',
      valid: passwordValue.length >= 8,
    },
    {
      label: 'Contain at least 1 uppercase letter',
      valid: /[A-Z]/.test(passwordValue),
    },
    {
      label: 'Contain at least 1 number',
      valid: /[0-9]/.test(passwordValue),
    },
    {
      label: 'Contain at least 1 special character',
      valid: /[!@#$%^&*(),.?":{}|<>]/.test(passwordValue),
    },
  ]

  // Keep branchAddressInput in sync with form value when going back/forward
  React.useEffect(() => {
    setBranchAddressInput(watch('branchAddress'))
  }, [activeStep, watch])

  return (
    <div
      style={{
        minHeight: '100vh',
        width: '100%',
        position: 'relative',
        background: 'linear-gradient(90deg, #ff6600 10%, #ff8800 100%)',
      }}
    >
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Container size="100%" px={8} style={{ maxWidth: 1000, width: '100%', margin: '0 auto' }}>
          <Paper
            withBorder
            shadow="md"
            p={30}
            mt={80}
            mb={120}
            radius="md"
            w="100%"
            style={{ maxWidth: 800, width: '100%', margin: '0 auto' }}
          >
            <Image h={40} w="auto" fit="contain" src="/carma_partner_logo.svg" alt="Carma Logo" />
            <Stack mt="md" gap={0}>
              {(activeStep === 0 || activeStep === 1) && (
                <Title order={3} style={{ fontWeight: 700, color: '#333' }}>
                  {(() => {
                    if (activeStep === 0) {
                      return 'Enter business details'
                    }
                    if (activeStep === 1) {
                      return 'Create user account'
                    }
                    return 'Enter business details'
                  })()}
                </Title>
              )}
              {activeStep === 2 ? (
                <Stack align="center" justify="center" style={{ minHeight: 450, width: '100%' }}>
                  <Title order={2} style={{ fontWeight: 600, color: '#222', marginTop: 16, marginBottom: 8 }}>
                    Payment processing setup
                  </Title>
                  <Text c="dimmed" mb={8} style={{ maxWidth: 450 }}>
                    The next step is to set up your Stripe account, the secure payment processor integrated with Carma.
                  </Text>
                  <Text c="dimmed" mb={32} style={{ maxWidth: 450 }}>
                    Please ensure all information is entered accurately and completely to facilitate smooth payment
                    processing and prevent potential delays in receiving your funds.
                  </Text>
                  <Button
                    component="a"
                    href={stripeLink || 'https://dashboard.stripe.com/register'}
                    size="md"
                    style={{ minWidth: 240, fontWeight: 600, fontSize: 18 }}
                    disabled={!stripeLink}
                  >
                    Set up your Stripe account
                  </Button>
                </Stack>
              ) : activeStep === 1 ? (
                <form onSubmit={onSubmitPersonal}>
                  <Stack mt="sm" gap="xs">
                    <TextInput
                      label="Email"
                      placeholder="Enter your email"
                      required
                      value={watchPersonal('email')}
                      onChange={(e) => setValuePersonal('email', e.target.value)}
                      error={errorsPersonal.email?.message}
                    />
                    <TextInput
                      label="Phone number"
                      placeholder="Enter your phone number"
                      required
                      value={watchPersonal('phone')}
                      onChange={(e) => {
                        let value = e.target.value.replace(/\D/g, '')
                        if (value.length > 10) value = value.slice(0, 10)
                        let formatted = value
                        if (value.length > 6) {
                          formatted = `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6)}`
                        } else if (value.length > 3) {
                          formatted = `(${value.slice(0, 3)}) ${value.slice(3)}`
                        } else if (value.length > 0) {
                          formatted = `(${value}`
                        }
                        setValuePersonal('phone', formatted)
                      }}
                      error={errorsPersonal.phone?.message}
                    />
                    <PasswordInput
                      label="Password"
                      placeholder="Enter your password"
                      required
                      value={watchPersonal('password')}
                      onChange={(e) => setValuePersonal('password', e.target.value)}
                      error={errorsPersonal.password?.message}
                    />
                    <Stack gap={0} mb="xs" mt={-8}>
                      {passwordChecks.map((check) => (
                        <Group key={check.label} gap={8} align="center">
                          {check.valid ? (
                            <IconCircleCheckFilled size={20} color="#2ecc40" />
                          ) : (
                            <IconCircleCheck size={20} color="#adb5bd" />
                          )}
                          <Text c={check.valid ? 'black' : 'gray'} size="sm">
                            {check.label}
                          </Text>
                        </Group>
                      ))}
                    </Stack>
                    <PasswordInput
                      label="Confirm password"
                      placeholder="Confirm your password"
                      required
                      value={watchPersonal('confirmPassword')}
                      onChange={(e) => setValuePersonal('confirmPassword', e.target.value)}
                      error={errorsPersonal.confirmPassword?.message}
                    />
                    <Group mt="xl" grow>
                      <Button variant="default" onClick={() => setActiveStep(0)}>
                        Back to business details
                      </Button>
                      <Button type="submit" loading={isRegistering}>
                        Submit
                      </Button>
                    </Group>
                    {isRegisterError && (
                      <Text c="red" size="sm" mt={8}>
                        {'Failed to register. Please try again. If the problem persists, please contact support.'}
                      </Text>
                    )}
                    <Text size="sm" c="dimmed" mt="sm">
                      By creating an account, you have read and agree to our{' '}
                      <a
                        href="https://www.joincarma.com/terms-of-use"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#888', fontWeight: 600 }}
                      >
                        Terms of Service
                      </a>{' '}
                      and{' '}
                      <a
                        href="https://www.joincarma.com/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#888', fontWeight: 600 }}
                      >
                        Privacy Policy
                      </a>
                    </Text>
                  </Stack>
                </form>
              ) : (
                <form onSubmit={onSubmit}>
                  <Stack mt="sm" gap="xs">
                    <TextInput
                      label="Legal entity name"
                      placeholder="Please enter as it appears on your business registration"
                      required
                      {...register('entityName')}
                      error={errors.entityName?.message}
                    />
                    <TextInput
                      label={'Branch name'}
                      placeholder="Enter first branch name"
                      required
                      {...register('branchName')}
                      error={errors.branchName?.message}
                    />
                    <TextInput
                      label="Branch email"
                      placeholder="Enter branch email"
                      required
                      {...register('branchEmail')}
                      error={errors.branchEmail?.message}
                    />
                    <Autocomplete
                      label="Branch address"
                      placeholder="Enter branch address"
                      required
                      value={branchAddressInput}
                      onChange={(value) => {
                        setBranchAddressInput(value)
                        setValue('branchAddress', value)
                      }}
                      onInput={(e) => {
                        setBranchAddressInput(e.currentTarget.value)
                      }}
                      data={Array.isArray(placesData?.items) ? placesData.items : []}
                      rightSection={isPlacesLoading ? <Loader size={18} /> : undefined}
                      rightSectionWidth={36}
                      error={isPlacesError ? 'Failed to autocomplete address' : errors.branchAddress?.message}
                    />
                    <TextInput
                      label="Branch phone number"
                      placeholder="Enter branch phone number"
                      required
                      value={watch('branchPhone')}
                      onChange={(e) => {
                        let value = e.target.value.replace(/\D/g, '')
                        if (value.length > 10) value = value.slice(0, 10)
                        let formatted = value
                        if (value.length > 6) {
                          formatted = `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6)}`
                        } else if (value.length > 3) {
                          formatted = `(${value.slice(0, 3)}) ${value.slice(3)}`
                        } else if (value.length > 0) {
                          formatted = `(${value}`
                        }
                        setValue('branchPhone', formatted)
                      }}
                      error={errors.branchPhone?.message}
                    />
                    <Select
                      label="Branch metro area"
                      placeholder="Select branch metro area"
                      required
                      data={metroAreas?.map((area) => ({ value: area.id, label: area.name })) || []}
                      error={
                        isMetroError
                          ? 'Failed to load available metro areas. Please refresh the page.'
                          : errors.branchMetro?.message
                      }
                      {...register('branchMetro')}
                      onChange={(value) => setValue('branchMetro', value || '')}
                      value={watch('branchMetro')}
                      disabled={isMetroError}
                    />
                    <MultiSelect
                      label="Serviceable engine types"
                      placeholder="Select engine types"
                      required
                      data={[
                        { label: 'Gasoline', value: 'GAS' },
                        { label: 'Diesel', value: 'DIESEL' },
                        { label: 'Electric', value: 'ELECTRIC' },
                      ]}
                      {...register('branchEngineTypes')}
                      error={errors.branchEngineTypes?.message}
                      onChange={(value) => setValue('branchEngineTypes', value)}
                      value={watch('branchEngineTypes')}
                    />
                    <MultiSelect
                      label="Serviceable vehicle classes"
                      placeholder="Select vehicle classes"
                      required
                      data={
                        vehicleClasses?.map((vc) => {
                          const formatNum = (n: number) => n.toLocaleString()
                          const from = typeof vc.weight.from === 'number' ? formatNum(vc.weight.from) : '?'
                          const to = typeof vc.weight.to === 'number' ? '-' + formatNum(vc.weight.to) : '+'
                          return {
                            value: vc.id,
                            label: `${vc.name} (${from}${to} lbs)`,
                          }
                        }) || []
                      }
                      error={
                        isVehicleClassesError
                          ? `Failed to load available vehicle classes. Please refresh the page.`
                          : errors.branchVehicleClasses?.message
                      }
                      onChange={(value) => setValue('branchVehicleClasses', value)}
                      value={watch('branchVehicleClasses')}
                      disabled={isVehicleClassesError}
                    />
                    <NumberInput
                      label="Labor rate"
                      placeholder="0"
                      required
                      min={0}
                      decimalScale={2}
                      step={1}
                      leftSection="$"
                      rightSection="per hour"
                      rightSectionWidth={90}
                      max={300}
                      error={errors.branchLaborRate?.message}
                      value={laborRateValue}
                      onChange={(value: string | number) => {
                        if (typeof value === 'number' && !isNaN(value)) setValue('branchLaborRate', value)
                      }}
                    />
                  </Stack>
                  <Group grow mt="xl">
                    <RouterButton to="/login" variant="default">
                      Back to sign in
                    </RouterButton>
                    <Button type="submit">Continue</Button>
                  </Group>
                </form>
              )}
            </Stack>
          </Paper>
        </Container>
      </div>
    </div>
  )
}
